import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { DateTime } from 'luxon';

import { DateFormatPatterns } from '@clearverify/constants';
import type { Nullable } from '@clearverify/types';

import { FileInfoFragment } from 'src/api';
import type { AlignTypeImageProcess, DroppedFile, FitTypeImageProcess } from 'src/types/file';

interface DownloadXlsxInput {
  tableExport: {
    blob: Blob;
  };
}

type DownloadFromTableInput = {
  downloadUrl: string;
  filename: string;
  companyName?: string;
}[];

export const downloadXlsx = (data: DownloadXlsxInput): { success: boolean } => {
  const downloadDate = DateTime.local().toFormat(DateFormatPatterns.fullDateWithTime);

  const xlsxBlob = data.tableExport.blob;
  const fileName = `${downloadDate}.xlsx`;

  saveAs(xlsxBlob, fileName);

  return { success: true };
};

export const downloadFilesFromTable = async (
  data: DownloadFromTableInput,
): Promise<{ success: boolean }> => {
  const zip = new JSZip();

  if (data.length === 0) {
    return { success: true };
  }
  if (data.length === 1 && data[0]) {
    saveAs(data[0].downloadUrl, data[0].filename);
    return { success: true };
  }
  try {
    for await (const file of data) {
      if (file.downloadUrl && file.filename) {
        zip
          .folder(`${file.companyName}`)
          ?.file(file.filename, (await fetch(file.downloadUrl)).blob(), { binary: true });
      }
    }
    const result = await zip.generateAsync({ type: 'blob' });
    saveAs(result, 'company_files.zip');

    return { success: true };
  } catch (error) {
    console.error(error);
    return { success: false };
  }
};

// The convertFileSize takes the file size in number of bytes as an argument
export const convertFileSize = (size: number): string => {
  if (!size) return '0b';

  // The returned value is a string representing the size of the file in kilobytes
  return `${(size / 1000).toFixed()} kb`;
};

export const formatBytes = (bytes: any, decimals = 2) => {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const convertFileToDropped = (
  file: Nullable<FileInfoFragment>,
  validationEnabled = false,
): DroppedFile | null => {
  if (!file) return null;

  const droppedFile: DroppedFile = {
    id: file.id || '',
    uploadedFileId: file.id,
    uploadedFileUrl: file.downloadUrl,
    uploadedFileName: file.filename,
    size: file.size,
    loading: false,
    loadingComplete: true,

    ...(validationEnabled
      ? {
          valid: true,
          validationComplete: true,
          validationInProgress: false,
        }
      : null),
  };

  return droppedFile;
};

export const downloadFileByDownloadUrl = (downloadUrl: Nullable<string>, fileName: string) => {
  if (!downloadUrl) return;
  saveAs(downloadUrl, fileName);
};

export const processFilestackImage = (
  src: Nullable<FileInfoFragment | string>,
  widthValue?: number,
  heightValue?: number,
  fitValue?: FitTypeImageProcess,
  alignValue?: AlignTypeImageProcess,
) => {
  if (typeof src === 'string') {
    return src;
  }
  if (src?.mime === 'image/svg+xml') {
    return src.downloadUrl as string;
  }

  const imageDownloadUrl = src?.downloadUrl as string;
  const tld = new URL(imageDownloadUrl).hostname.split('.').at(-1);
  const splittedUrl = imageDownloadUrl.split(`.${tld}`);

  const width = widthValue ? `w:${widthValue}` : '';
  const height = heightValue ? `,h:${heightValue}` : '';
  const fit = fitValue ? `,fit:${fitValue}` : '';
  const align = alignValue ? `,align:${alignValue}` : '';
  const resizeParams = `.com/resize=${width}${height}${fit}${align}`;

  splittedUrl[0] += resizeParams;
  const resizedUrl = splittedUrl.join('');

  return resizedUrl;
};
